import { Filter, Search, MapPin, Briefcase, ChevronDown, Clock } from 'lucide-react';

const FilterBar = ({ onSearch, onFilterChange, activeFilter }) => {
  const categories = [
    { name: 'All Roles', count: null },
    { name: 'Engineering', count: null },
    { name: 'Design', count: null },
    { name: 'Marketing', count: null },
    { name: 'Sales', count: null },
    { name: 'Management', count: null },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm space-y-8">
        {/* Header */}
        <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
          <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-primary">
            <Filter className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-h4 font-black text-primary leading-none">Refine Search</h2>
            <p className="text-tiny font-bold text-slate-400 uppercase tracking-widest mt-1">Find your perfect role</p>
          </div>
        </div>

        {/* Keyword Search */}
        <div className="space-y-2">
          <label className="text-tiny font-black text-slate-400 uppercase tracking-[0.2em]">Search Keywords</label>
          <div className="relative group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-primary transition-colors" />
            <input 
              type="text"
              placeholder="Job title, skills..."
              className="w-full bg-slate-50 border border-slate-200 text-slate-700 text-subtext rounded-xl pl-11 pr-4 py-3.5 outline-none focus:bg-white focus:border-primary focus:ring-4 focus:ring-blue-500/5 transition-all"
              onChange={(e) => onSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Department Filter */}
        <div className="space-y-3">
          <label className="text-tiny font-black text-slate-400 uppercase tracking-[0.2em]">Departments</label>
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat.name}
                onClick={() => onFilterChange('department', cat.name === 'All Roles' ? '' : cat.name)}
                className={`px-4 py-2 rounded-xl text-subtext font-bold transition-all ${
                  (activeFilter === cat.name || (!activeFilter && cat.name === 'All Roles'))
                  ? 'bg-primary text-white shadow-lg shadow-blue-500/20' 
                  : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Work Model Filter */}
        <div className="space-y-4">
          <label className="text-tiny font-black text-slate-400 uppercase tracking-widest pl-1">Work Model</label>
          <div className="relative group">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors pointer-events-none">
              <Clock className="w-4 h-4" />
            </div>
            <select
              onChange={(e) => onFilterChange('work_model', e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 p-4 pl-11 rounded-2xl outline-none focus:border-blue-400 focus:bg-white transition-all appearance-none text-subtext font-bold text-primary cursor-pointer shadow-sm hover:shadow-md"
            >
              <option value="">All Models</option>
              <option value="Onsite">Onsite</option>
              <option value="Remote">Remote</option>
              <option value="Hybrid">Hybrid</option>
            </select>
            <div className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none group-focus-within:rotate-180 transition-transform">
              <ChevronDown className="w-4 h-4" />
            </div>
          </div>
        </div>

        {/* Employment Type Filter */}
        <div className="space-y-4">
          <label className="text-tiny font-black text-slate-400 uppercase tracking-widest pl-1">Employment Type</label>
          <div className="relative group">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors pointer-events-none">
              <Briefcase className="w-4 h-4" />
            </div>
            <select
              onChange={(e) => onFilterChange('type', e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 p-4 pl-11 rounded-2xl outline-none focus:border-blue-400 focus:bg-white transition-all appearance-none text-subtext font-bold text-primary cursor-pointer shadow-sm hover:shadow-md"
            >
              <option value="">All Types</option>
              <option value="Full-Time">Full-Time</option>
              <option value="Part-Time">Part-Time</option>
              <option value="Contract">Contract</option>
              <option value="Internship">Internship</option>
            </select>
            <div className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none group-focus-within:rotate-180 transition-transform">
              <ChevronDown className="w-4 h-4" />
            </div>
          </div>
        </div>

        {/* Experience Level Filter */}
        <div className="space-y-4">
          <label className="text-tiny font-black text-slate-400 uppercase tracking-widest pl-1">Experience Level</label>
          <div className="relative group">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors pointer-events-none">
              <Briefcase className="w-4 h-4" />
            </div>
            <select
              onChange={(e) => onFilterChange('experience_level', e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 p-4 pl-11 rounded-2xl outline-none focus:border-blue-400 focus:bg-white transition-all appearance-none text-subtext font-bold text-primary cursor-pointer shadow-sm hover:shadow-md"
            >
              <option value="">All Levels</option>
              <option value="Junior">Entry / Junior</option>
              <option value="Mid-Level">Mid-Level</option>
              <option value="Senior">Senior</option>
              <option value="Lead">Lead / Manager</option>
            </select>
            <div className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none group-focus-within:rotate-180 transition-transform">
              <ChevronDown className="w-4 h-4" />
            </div>
          </div>
        </div>

        {/* Location Filters */}
        <div className="space-y-4">
          <label className="text-tiny font-black text-slate-400 uppercase tracking-widest pl-1">Location Details</label>
          <div className="grid grid-cols-1 gap-3">
            <div className="relative group">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                <MapPin className="w-4 h-4" />
              </div>
              <input 
                type="text"
                placeholder="State / Province"
                onChange={(e) => onFilterChange('state', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 p-3 pl-11 rounded-xl outline-none focus:border-blue-400 focus:bg-white transition-all text-subtext font-bold text-primary shadow-sm"
              />
            </div>
            <div className="relative group">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                <MapPin className="w-4 h-4" />
              </div>
              <input 
                type="text"
                placeholder="City"
                onChange={(e) => onFilterChange('city', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 p-3 pl-11 rounded-xl outline-none focus:border-blue-400 focus:bg-white transition-all text-subtext font-bold text-primary shadow-sm"
              />
            </div>
          </div>
        </div>

        <button 
          onClick={() => window.location.reload()} 
          className="w-full py-4 text-tiny font-black text-slate-400 hover:text-red-500 border border-dashed border-slate-200 rounded-2xl transition-all uppercase tracking-widest hover:border-red-200 hover:bg-red-50"
        >
          Reset All Filters
        </button>
      </div>
    </div>
  );
};

export default FilterBar;
